import React, {useContext, useEffect, useRef, useState} from "react";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {dateFormatter, i18nConvertNumberWithDecimals, useActionColDef,} from "../../utils";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../constants";
import SectionHeader from "../../../../util/component/SectionHeader";
import {Box, Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import {AgGridReact} from "ag-grid-react";
import DialogComponent from "../../common/components/DialogComponent";
import {useAgGridLocale} from "../../../../config/globalConfig";
import {ColDef, ICellRendererParams} from "ag-grid-community";
import dayjs from "dayjs";
import {FormSearchRateModel, RateEntityResponse} from "../../api/types/rate";
import Loading from "../../common/components/Loading";
import {useRateRepository} from "../../api/repositories/RateRepository";
import RateFilters from "./Filters/RateFilters";
import RateContainerView from "../Rate/RateContainerView";
import {DialogInterface, SelectElementModelBE, SelectElementModelFE} from "../../api/types/generic";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import ActionsPopover from "../../common/components/ActionsPopover";
import BasicToolbar from "../../common/components/BasicToolbar";
import useAgGridCustomHook from "../../hooks/useAgGridCustomHook";
import {useRateDetailRepository} from "../../api/repositories/RateDetailRepository";
import {useSearchParams} from "react-router-dom";
import CustomLoader from "../../../../util/component/CustomLoader";

const Rates = () => {

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    const [queryParameters] = useSearchParams();

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Repository Hooks
    const {getRatesPaged, deleteRate, terminateRate} = useRateRepository();
    const {exportJimpyRateExcel, exportRateExcel} = useRateDetailRepository();

    // Use State
    const [viewRate, setViewRate] = useState<boolean>(false);
    const [rowIndex, setRowIndex] = useState<number | null>(null);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);

    const handleGetRatePage = async () => {
        gridRef.current!.api.refreshServerSide({purge: true});
    };

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchRateModel>({
        id: "",
        name: "",
        commercialCategoryId: null,
        commercialSegmentId: null,
        tpUnit: "",
        pickupDateFrom: "",
        pickupDateTo: "",
        reservationFrom: "",
        reservationTo: "",
        active: false,
        dealers: dealerOption,
        legalEntities: legalEntityOptions
    })

    // Use Grid Data Custom Hook
    const {setServerSideDatasource, handleTriggerFilters} = useAgGridCustomHook<
        RateEntityResponse,
        FormSearchRateModel
    >({
        gridRef,
        api: getRatesPaged,
        initialSortColId: "id",
        entityFilters: testFilters,
        customSort: "RateEntityResponse",
    });

    useEffect(() => {
        if (gridRef?.current) {
            handleTriggerFilters({
                commercialCategoryId: (testFilters.commercialCategoryId as SelectElementModelFE)?.code?.toString() || "",
                commercialSegmentId: (testFilters.commercialSegmentId as SelectElementModelFE)?.code?.toString() || "",
                tpUnit: testFilters.tpUnit as string || "",
                pickupDateFrom: testFilters.pickupDateFrom ? dayjs(testFilters.pickupDateFrom).toISOString() : testFilters.pickupDateFrom,
                pickupDateTo: testFilters.pickupDateTo ? dayjs(testFilters.pickupDateTo).toISOString() : testFilters.pickupDateTo,
                reservationFrom: testFilters.reservationFrom ? dayjs(testFilters.reservationFrom).toISOString() : testFilters.reservationFrom,
                reservationTo: testFilters.reservationTo ? dayjs(testFilters.reservationTo).toISOString() : testFilters.reservationTo,
                name: testFilters.name,
                id: testFilters.id,
                showOnlyActive: testFilters.showOnlyActive,
                legalEntities: Array.isArray(testFilters.legalEntities) 
                    ? (testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)
                    : [],
                dealers: Array.isArray(testFilters.dealers)
                    ? (testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)
                    : []
            });
        }
    }, [testFilters.showOnlyActive]);

    useEffect(() => {
        handleTriggerResetFilters();
    }, []);

    const handleTriggerResetFilters = () => {
        setTestFilters({
            id: "",
            name: "",
            commercialCategoryId: null,
            commercialSegmentId: null,
            tpUnit: "",
            pickupDateFrom: "",
            pickupDateTo: "",
            reservationFrom: "",
            reservationTo: "",
            active: false,
            dealers: dealerOption,
            legalEntities: legalEntityOptions
        });
        handleTriggerFilters({
            id: "",
            name: "",
            commercialCategoryId: null,
            commercialSegmentId: null,
            tpUnit: "",
            pickupDateFrom: "",
            pickupDateTo: "",
            reservationFrom: "",
            reservationTo: "",
            active: false,
            dealers: dealerFilteredOptions,
            legalEntities: legalEntityOptions.map(le => le.code as string)
        });
    };

    const handleTriggerFiltersInitial = () => {
        handleTriggerFilters({
            commercialCategoryId:
                (
                    testFilters.commercialCategoryId as SelectElementModelFE
                )?.code?.toString() || "",
            commercialSegmentId:
                (
                    testFilters.commercialSegmentId as SelectElementModelFE
                )?.code?.toString() || "",
            tpUnit: (testFilters.tpUnit as string) || "",
            pickupDateFrom: testFilters.pickupDateFrom
                ? dayjs(testFilters.pickupDateFrom).toISOString()
                : testFilters.pickupDateFrom,
            pickupDateTo: testFilters.pickupDateTo
                ? dayjs(testFilters.pickupDateTo).toISOString()
                : testFilters.pickupDateTo,
            reservationFrom: testFilters.reservationFrom
                ? dayjs(testFilters.reservationFrom).toISOString()
                : testFilters.reservationFrom,
            reservationTo: testFilters.reservationTo
                ? dayjs(testFilters.reservationTo).toISOString()
                : testFilters.reservationTo,
            id: testFilters.id,
            name: testFilters.name,
            legalEntities: Array.isArray(testFilters.legalEntities) 
                ? (testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)
                : [],
            dealers: Array.isArray(testFilters.dealers)
                ? (testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)
                : []
        });
    };

    // useEffect(() => {
    //     setTimeout(() => {
    //         handleTriggerFiltersInitial();
    //     }, 500);
    // }, [i18n.resolvedLanguage]);

    const viewButtonClicked = (data: RateEntityResponse) => {
        setRowIndex(data.id);
        setViewRate(true);
    };

    const updateButtonClicked = (data: RateEntityResponse) => {
        //navigate("../rate/base-rates/create", { replace: true, state: { rateId: data.id } });
    };

    const terminate = async (id: number) => {
        await terminateRate(id);
        setDialog(null);
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        handleGetRatePage();
    };

    const terminateButtonClicked = async (data: RateEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => terminate(data.id),
            message: t("pricing:rates.dialogContents.terminateRateInfo"),
        });
    };

    const deleteButtonClicked = (data: RateEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => handleConfirmDelete(data.id),
            message: t("pricing:rates.dialogContents.removeRateInfo"),
        });
        setRowIndex(data.id);
    };

    const downloadJimpyButtonClicked = async (data: RateEntityResponse) => {
        await exportJimpyRateExcel(data)
    }

    const downloadButtonClicked = async (data: RateEntityResponse) => {
        await exportRateExcel(data.id, data.legalEntity)
    }

    const {actionColDef} = useActionColDef();
    const columnDefs: ColDef[] = [
        {
            field: "id",
            headerName: t("pricing:rate.form.labels.mainDefinitionLabels.id"),
            headerTooltip: t("pricing:rate.form.labels.mainDefinitionLabels.id"),
            flex: 1,
            minWidth: 100,
            filter: "text",
            pinned: "left"
        },
        {
            field: "legalEntities",
            headerName: t(
                "pricing:rate.form.labels.mainDefinitionLabels.legalEntity"
            ),
            headerTooltip: t(
                "pricing:rate.form.labels.mainDefinitionLabels.legalEntities"
            ),
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:legalEntities.'.concat(params.data.legalEntity)),
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            field: "dealers",
            headerName: t("pricing:rate.form.labels.mainDefinitionLabels.dealer"),
            headerTooltip: t("pricing:rate.form.labels.mainDefinitionLabels.dealer"),
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:dealers.'.concat(params.data.dealer)),
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            field: "name",
            headerName: t("pricing:rate.form.labels.mainDefinitionLabels.name"),
            headerTooltip: t("pricing:rate.form.labels.mainDefinitionLabels.name"),
            flex: 1,
            minWidth: 100,
            filter: "text"
        },
        {
            field: "commercialSegmentId",
            headerName: t("pricing:rate.form.labels.mainDefinitionLabels.commercialSegment"),
            headerTooltip: t("pricing:rate.form.labels.mainDefinitionLabels.commercialSegment"),
            flex: 1,
            minWidth: 100,
            cellRenderer: (params: ICellRendererParams) => (
                params.data.commercialCategory?.commercialSegment.name
            ),
            filter: "text",
            hide: true
        },
        {
            field: "description",
            headerName: t("pricing:excessGroup.form.labels.description"),
            headerTooltip: t("pricing:excessGroup.form.labels.description"),
            flex: 1,
            minWidth: 100,
        },
        {
            field: "commercialCategoryId",
            headerName: t("pricing:rate.form.labels.mainDefinitionLabels.commercialCategory"),
            headerTooltip: t("pricing:rate.form.labels.mainDefinitionLabels.commercialCategory"),
            flex: 1,
            minWidth: 100,
            cellRenderer: (params: ICellRendererParams) => {
                const category = params.data?.commercialCategory;
                const legalEntity = params.data?.legalEntity;
                
                if (!category) {
                    return '';
                }
                
                if (!category.labels || category.labels.length === 0) {
                    return category.name || '';
                }
                
                const labelObj = category.labels.find((l: any) => l.country === legalEntity);
                return labelObj?.label || category.name || '';
            },
            filter: "text"
        },
        {
            field: "reservationFrom",
            headerName: t("pricing:rate.form.labels.mainDefinitionLabels.reservationFrom"),
            headerTooltip: t("pricing:rate.form.labels.mainDefinitionLabels.reservationFrom"),
            flex: 1,
            minWidth: 100,
            valueFormatter: (params: any) => params.data.reservationFrom ? dateFormatter(params.data.reservationFrom) : "",
            filter: "text"
        },
        {
            field: "reservationTo",
            headerName: t("pricing:rate.form.labels.mainDefinitionLabels.reservationTo"),
            headerTooltip: t("pricing:rate.form.labels.mainDefinitionLabels.reservationTo"),
            flex: 1,
            minWidth: 100,
            valueFormatter: (params: any) => params.data.reservationTo ? dateFormatter(params.data.reservationTo) : "",
            filter: "text"
        },
        {
            field: "pickupDateFrom",
            headerName: t("pricing:rate.form.labels.mainDefinitionLabels.pickupDateFrom"),
            headerTooltip: t("pricing:rate.form.labels.mainDefinitionLabels.pickupDateFrom"),
            flex: 1,
            minWidth: 100,
            valueFormatter: (params: any) => params.data.pickupDateFrom ? dateFormatter(params.data.pickupDateFrom) : "",
            filter: "text"
        },
        {
            field: "pickupDateTo",
            headerName: t("pricing:rate.form.labels.mainDefinitionLabels.pickupDateTo"),
            headerTooltip: t("pricing:rate.form.labels.mainDefinitionLabels.pickupDateTo"),
            flex: 1,
            minWidth: 100,
            valueFormatter: (params: any) => params.data.pickupDateTo ? dateFormatter(params.data.pickupDateTo) : "",
            filter: "text"
        },
        {
            field: "referenceTariff.name",
            headerName: t("pricing:rate.form.labels.referenceTariffLabels.rate"),
            headerTooltip: t("pricing:rate.form.labels.referenceTariffLabels.rate"),
            flex: 1,
            minWidth: 100,
            sortable: false
        },
        {
            field: "relatedDiscountIncreasePercentage",
            headerName: t("pricing:rate.form.labels.referenceTariffLabels.discountIncrease"),
            headerTooltip: t("pricing:rate.form.labels.referenceTariffLabels.discountIncrease"),
            flex: 1,
            minWidth: 100,
            cellStyle: {
                justifyContent: "center",
                textAlign: "center"
            },
            cellRenderer: (params: any) => {
                return params.data.relatedDiscountIncreasePercentage ?
                    params.data.relatedDiscountIncreasePercentage + "%"
                    :
                    params.data.relatedDiscountIncreaseFixed
                        ?
                        i18nConvertNumberWithDecimals(Number(params.data.relatedDiscountIncreaseFixed) / 100)
                        :
                        null;
            },
            filter: "text",
            sortable: false
        },
        {
            ...actionColDef,
            cellRenderer: (params: ICellRendererParams) => (
                <ActionsPopover
                    url={`../rate/base-rates/create/${params.data.id}`}
                    params={params}
                    downloadJimpy={{action: downloadJimpyButtonClicked}}
                    download={{action: downloadButtonClicked}}
                    view={{
                        action: viewButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_VIEW_DETAIL_RATE_CONTAINER"
                            })
                        })
                    }}
                    update={{
                        action: updateButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_UPDATE_RATE_CONTAINER"
                            })
                        })
                    }}
                    del={{
                        action: deleteButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_DELETE_RATE_CONTAINER"
                            })
                        })
                    }}
                    terminate={{
                        action: terminateButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_TERMINATE_RATE_CONTAINER"
                            })
                        })
                    }}
                />
            )
        },
        {
            field: "tpUnit",
            filter: "text",
            hide: true
        },
        {
            field: "showOnlyActive",
            filter: "text",
            hide: true
        }
    ];

    const defaultColDef = DEFAULT_COL_DEF;

    const handleConfirmDelete = async (id: number) => {
        try {
            if (id) {
                await deleteRate(id);
                setDialog(null);
                await handleGetRatePage();
                dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            }
        } catch (e) {
        }
    };

    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                addIcon={
                    !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_CREATE_RATE_CONTAINER";
                        });
                    }) ?
                        {
                            onClick: () => {
                            }
                        } : undefined}
                activeSwitch={{
                    label: t("pricing:rentalPeriods.form.labels.showActive"),
                    handleChange: (checked: boolean) => setTestFilters({
                        ...testFilters,
                        showOnlyActive: checked
                    }),
                    value: testFilters.showOnlyActive as boolean
                }}
                url={"../rate/base-rates/create/new"}
            />
        )
    };

    const handleOnReady = async (e: any) => {
        const rateId = queryParameters.get('id');
        await setTestFilters({
            id: rateId ?? "",
            name: "",
            commercialCategoryId: null,
            commercialSegmentId: null,
            tpUnit: "",
            pickupDateFrom: "",
            pickupDateTo: "",
            reservationFrom: "",
            reservationTo: "",
            active: false,
            dealers: dealerOption,
            legalEntities: legalEntityOptions
        });
        handleTriggerFilters({
            id: rateId ?? "",
            name: "",
            commercialCategoryId: null,
            commercialSegmentId: null,
            tpUnit: "",
            pickupDateFrom: "",
            pickupDateTo: "",
            reservationFrom: "",
            reservationTo: "",
            active: false,
            dealers: dealerFilteredOptions,
            legalEntities: legalEntityOptions.map(le => le.code as string)
        });

        setServerSideDatasource(e);
    }

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_RATE.name")}/>
            <RateFilters
                handleGetRateList={handleTriggerFiltersInitial}
                handleClickResetSearch={handleTriggerResetFilters}
                testFilters={testFilters}
                setTestFilters={setTestFilters}
            />
            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        toolbar={toolbar}
                        pagination
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onGridReady={handleOnReady}
                    />

                    <RateContainerView rateId={rowIndex} open={viewRate} setOpen={setViewRate}/>

                    {dialog?.open &&
                        <DialogComponent
                            type={"info"}
                            onClickConfirm={dialog.onClickConfirm}
                            setOpen={() => setDialog(null)}
                            open={dialog.open}
                            message={dialog.message}
                        />
                    }
                </Grid>
            </Grid>

            <Loading/>
        </Box>
    );
};

export default Rates;
